import { useState, useEffect } from 'react';
import styles from '../emergencyAlert.module.css';
import AIEduDialog from '../../../components/Dialog/AIEduDialog';
import AIEduDialogFooter from '../../../components/Footer/AIEduDialogFooter';
import { useAuthenticate } from '../../../hooks';
import { API } from '../../../services';
import { showErrorToast } from '../../../services/setup/ApiResponseHandler';

const TagSelectionDialog = ({
    visible,
    onHide,
    onConfirm,
    f,
    loading = false,
    selectedTags = [],
}) => {
    const [availableCategories, setAvailableCategories] = useState([]);
    const [selectedAvailableTags, setSelectedAvailableTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { organizationId } = organizations?.[0] ?? {};
    const { placeId } = place ?? {};

    const loadAvailableTags = async () => {
        setLoadingTags(true);
        try {
            const { data } = await API.getAllTagsAndCategories(organizationId, placeId);
            
            // Get IDs of already selected tags
            const selectedTagIds = selectedTags.map(tag => tag.tagId || tag.id);
            
            const categoriesData = data.map((category) => {
                const availableTags = category.tags
                    .filter((tag) => !selectedTagIds.includes(tag.tagId)) // Filter out already selected tags
                    .map((tag) => ({
                        id: tag.tagId,
                        name: tag.tagName,
                    }));
                
                return {
                    id: category.tagsetCategoryId,
                    name: category.tagsetCategoryName,
                    expanded: true, // Expand first category by default
                    tags: availableTags,
                    allTagsSelected: availableTags.length === 0 && category.tags.length > 0,
                };
            });
            
            setAvailableCategories(categoriesData);
            setLoadingTags(false);
        } catch (error) {
            showErrorToast(f({ id: 'AI_EDU_ERROR_FETCHING_TAGS' }));
            setAvailableCategories([]);
            setLoadingTags(false);
        }
    };

    const toggleCategory = (categoryId) => {
        setAvailableCategories((prev) =>
            prev.map((category) =>
                category.id === categoryId
                    ? { ...category, expanded: !category.expanded }
                    : category
            )
        );
    };

    const handleTagSelection = (tagId) => {
        setSelectedAvailableTags((prev) =>
            prev.includes(tagId)
                ? prev.filter((id) => id !== tagId)
                : [...prev, tagId]
        );
    };

    const handleConfirm = () => {
        if (selectedAvailableTags.length === 0) {
            showErrorToast(f({ id: 'AI_EDU_SELECT_AT_LEAST_ONE_TAG' }));
            return;
        }

        // Get all selected tag objects from all categories
        const allTags = availableCategories.flatMap(
            (category) => category.tags
        );
        const tagsToAdd = allTags.filter((tag) =>
            selectedAvailableTags.includes(tag.id)
        );

        onConfirm(tagsToAdd);
    };

    const handleCancel = () => {
        setSelectedAvailableTags([]);
        onHide();
    };

    // Load available tags when dialog opens or when selectedTags change
    useEffect(() => {
        if (visible) {
            loadAvailableTags();
        }
    }, [visible, selectedTags]);

    const TagSelectionDialogContent = () => (
        <div className={styles.tag_selection_dialog}>
            <div className={styles.tag_selection_message}>
                {f({ id: 'AI_EDU_SELECT_TAGS_MESSAGE' })}
            </div>
            <div className={styles.available_tags_list}>
                {loadingTags ? (
                    <div className={styles.loading_tags}>
                        <i className='pi pi-spin pi-spinner' />
                        <span>{f({ id: 'AI_EDU_LOADING_TAGS' })}</span>
                    </div>
                ) : availableCategories.length === 0 ? (
                    <div className={styles.no_tags_available}>
                        {f({ id: 'AI_EDU_NO_TAGS_AVAILABLE' })}
                    </div>
                ) : (
                    availableCategories.map((category) => (
                        <div
                            key={category.id}
                            className={styles.workspace_container}
                        >
                            <div
                                className={styles.workspace_header}
                                onClick={() => toggleCategory(category.id)}
                            >
                                <i
                                    className={`pi ${
                                        category.expanded
                                            ? 'pi-sort-down-fill'
                                            : 'pi-caret-right'
                                    }`}
                                />
                                <span className={styles.workspace_name}>
                                    {category.name}
                                </span>
                            </div>
                            {category.expanded && category.tags.length > 0 && (
                                <div className={styles.workspace_tags}>
                                    {category.tags.map((tag) => (
                                        <div
                                            key={tag.id}
                                            className={`${
                                                styles.tag_selection_item
                                            } ${
                                                selectedAvailableTags.includes(
                                                    tag.id
                                                )
                                                    ? styles.tag_selected
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                handleTagSelection(tag.id)
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.tag_selection_label
                                                }
                                            >
                                                {tag.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    const footer = (
        <AIEduDialogFooter
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            confirmLabel={f({ id: 'AI_EDU_ADD_SELECTED_TAGS' })}
            cancelLabel={f({ id: 'AI_EDU_CANCEL' })}
            loading={loading}
        />
    );

    return (
        <AIEduDialog
            visible={visible}
            onHide={handleCancel}
            title={f({ id: 'AI_EDU_SELECT_TAGS' })}
            content={<TagSelectionDialogContent />}
            footer={footer}
            style={{ width: '50vw', maxWidth: '600px' }}
            draggable={false}
            modal
            closable
            dismissableMask
        />
    );
};

export default TagSelectionDialog;
